import { AuthContextProps } from '../../../../../contexts/AuthContext';

export interface User {
  businessPhones: string[];
  displayName: string;
  givenName: string | null;
  jobTitle: string | null;
  mail: string;
  mobilePhone: string | null;
  officeLocation: string | null;
  preferredLanguage: string | null;
  surname: string | null;
  userPrincipalName: string;
  id: string;
}

export type ActivityType =
  | 'update'
  | 'create'
  | 'delete'
  | 'comment'
  | 'system';
export type ActivityStatus = 'pending' | 'completed' | 'failed';
export type ActivityAction =
  | 'UserAssigned'
  | 'SectionCompleted'
  | 'StatusChanged'
  | 'EmailSent';

export interface Activity {
  id: number;
  workflowId: number;
  action: ActivityAction;
  userId: string;
  displayName: string;
  email: string;
  timestamp: string;
  details: string;
  previousStatus?: string;
  newStatus?: string;
  sectionId: number;
  fieldId?: number;
  emailContent?: {
    subject: string;
    html: string;
    to: string;
  };
}

export interface Client {
  clientName: string;
  clientCode: string;
  clientUno: string;
}

export interface Matter {
  matterName: string;
  matterCode: string;
  matterUno: string;
}

export interface FetchDataProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  searchValue?: string;
  spoofedUserEmail?: string;
  office?: string;
  country?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginatedItems<T> {
  items: T[];
}

export interface GraphDataHook {
  fetchFeeEarners: (
    searchTerm: string,
    office?: string,
  ) => Promise<User[] | null>;
  fetchPartners: (
    searchTerm: string,
    office?: string,
  ) => Promise<User[] | null>;
  fetchUsers: (searchTerm: string, office?: string) => Promise<User[] | null>;
}

/**
 * Enum representing different types of payment approval workflows
 * @enum {number}
 */
export enum PaymentWorkflowType {
  /** Default/unspecified workflow type */
  Undefined = 0,

  /** UK payment approval workflow */
  // UKPaymentApproval = 1,

  /** Dubai standard payment approval workflow */
  DubaiPaymentApproval = 2,

  /** Dubai transfer requisition workflow */
  DubaiTransferRequisition = 3,

  /** Dubai payment from office account workflow */
  DubaiPaymentFromOfficeAcc = 4,

  /** Overtime approval workflow */
  OvertimeApproval = 5,
  GiftHospitalityDonationUk = 6,
  GiftHospitalityDonationDubai = 7,
  GiftHospitalityDonationDublin = 8,
  // PLOP_INJECT_WORKFLOW_ENUM
}

export const SECTION_KEY_MAP: Record<string, string> = {
  'dubai-payment-from-office-acc': 'PaymentfromOfficeAccForm',
  'dubai-payment-approval': 'DubaiPaymentApprovalForm',
  'dubai-transfer-requisition': 'DubaiTransferRequisitionForm',
  'overtime-approval': 'OvertimeApprovalForm',
  'gift-hospitality-donation-uk': 'GiftHospitalityDonationUkForm',
  'gift-hospitality-donation-dubai': 'GiftHospitalityDonationDubaiForm',
  'gift-hospitality-donation-dublin': 'GiftHospitalityDonationDublinForm',
  // PLOP_INJECT_SECTION_KEY_MAP
};

export const FORM_TO_SECTION_KEY_MAP: Record<string, string> = {
  DubaiPaymentFromOfficeAcc: 'dubai-payment-from-office-acc',
  DubaiPaymentApproval: 'dubai-payment-approval',
  DubaiTransferRequisition: 'dubai-transfer-requisition',
  OvertimeApproval: 'overtime-approval',
  GiftHospitalityDonationUk: 'gift-hospitality-donation-uk',
  GiftHospitalityDonationDubai: 'gift-hospitality-donation-dubai',
  GiftHospitalityDonationDublin: 'gift-hospitality-donation-dublin',
  // PLOP_INJECT_FORM_TO_SECTION_KEY_MAP
};

/**
 * Converts a workflow type input (string or number) to the corresponding PaymentWorkflowType enum value
 * @param typeInput - String or number representation of a workflow type
 * @returns The corresponding PaymentWorkflowType enum value
 */
export const getWorkflowTypeFromValue = (
  typeInput: string | number | PaymentWorkflowType,
): PaymentWorkflowType => {
  // If it's already a number, check if it's a valid enum value
  if (typeof typeInput === 'number') {
    return isValidPaymentWorkflowType(typeInput)
      ? typeInput
      : PaymentWorkflowType.Undefined;
  }

  // If it's a string, try to convert it
  if (typeof typeInput === 'string') {
    // Check if it's a numeric string
    const numValue = Number(typeInput);
    if (!isNaN(numValue) && isValidPaymentWorkflowType(numValue)) {
      return numValue;
    }

    // Create a mapping from display names to enum values
    const nameToTypeMap: Record<string, PaymentWorkflowType> = {
      Undefined: PaymentWorkflowType.Undefined,
      // 'UKPaymentApproval': PaymentWorkflowType.UKPaymentApproval,
      DubaiPaymentApproval: PaymentWorkflowType.DubaiPaymentApproval,
      DubaiTransferRequisition: PaymentWorkflowType.DubaiTransferRequisition,
      DubaiPaymentFromOfficeAcc: PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
      OvertimeApproval: PaymentWorkflowType.OvertimeApproval,
      GiftHospitalityDonationUk: PaymentWorkflowType.GiftHospitalityDonationUk,
      GiftHospitalityDonationDubai:
        PaymentWorkflowType.GiftHospitalityDonationDubai,
      GiftHospitalityDonationDublin:
        PaymentWorkflowType.GiftHospitalityDonationDublin,
      // PLOP_INJECT_WORKFLOW_NAME_MAP
      Unknown: PaymentWorkflowType.Undefined,
    };

    return nameToTypeMap[typeInput] || PaymentWorkflowType.Undefined;
  }

  // Default case - it's already a PaymentWorkflowType enum value
  return typeInput;
};

// Example type guard for type safety
export const isValidPaymentWorkflowType = (
  value: number,
): value is PaymentWorkflowType => {
  return Object.values(PaymentWorkflowType).includes(value);
};

export const getWorkflowTypeFromDisplayName = (
  displayName: string,
): PaymentWorkflowType => {
  // Create a mapping from display names to enum values
  const displayNameToTypeMap: Record<string, PaymentWorkflowType> = {
    // 'UK Payment Approval': PaymentWorkflowType.UKPaymentApproval,
    'Client Account Payment Request': PaymentWorkflowType.DubaiPaymentApproval,
    'Transfer Requisition': PaymentWorkflowType.DubaiTransferRequisition,
    'Payment From Office Account':
      PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
    'Overtime Approval': PaymentWorkflowType.OvertimeApproval,
    'Gift/Hospitality/Donation (UK)': PaymentWorkflowType.GiftHospitalityDonationUk,
    'Gift/Hospitality/Donation (Dubai)':
      PaymentWorkflowType.GiftHospitalityDonationDubai,
    'Gift/Hospitality/Donation (Dublin)':
      PaymentWorkflowType.GiftHospitalityDonationDublin,
    // PLOP_INJECT_WORKFLOW_DISPLAY_MAP
  };

  return displayNameToTypeMap[displayName] || PaymentWorkflowType.Undefined;
};

// Helper function to get workflow type name
export const getWorkflowTypeName = (type: PaymentWorkflowType): string => {
  switch (type) {
    case PaymentWorkflowType.Undefined:
      return 'Undefined';
    // case PaymentWorkflowType.UKPaymentApproval:
    //   return 'UK Payment Approval';
    case PaymentWorkflowType.DubaiPaymentApproval:
      return 'Client Account Payment Request';
    case PaymentWorkflowType.DubaiTransferRequisition:
      return 'Transfer Requisition';
    case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
      return 'Payment From Office Account';
    case PaymentWorkflowType.OvertimeApproval:
      return 'Overtime Approval';
    case PaymentWorkflowType.GiftHospitalityDonationUk:
      return 'Gift/Hospitality/Donation (UK)';
    case PaymentWorkflowType.GiftHospitalityDonationDubai:
      return 'Gift/Hospitality/Donation (Dubai)';
    case PaymentWorkflowType.GiftHospitalityDonationDublin:
      return 'Gift/Hospitality/Donation (Dublin)';
    // PLOP_INJECT_WORKFLOW_TYPE_NAME
    default:
      return 'Unknown';
  }
};

/**
 * Checks if a workflow type requires client and matter data
 * @param workflowType - The workflow type to check
 * @returns true if the workflow requires client/matter data, false otherwise
 */
const WORKFLOWS_WITHOUT_CLIENT_MATTER: readonly PaymentWorkflowType[] = [
  PaymentWorkflowType.DubaiTransferRequisition,
  PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
  PaymentWorkflowType.OvertimeApproval,
  PaymentWorkflowType.GiftHospitalityDonationUk,
  PaymentWorkflowType.Undefined,
  PaymentWorkflowType.GiftHospitalityDonationDubai,
  PaymentWorkflowType.GiftHospitalityDonationDublin,
  // PLOP_INJECT_NO_CLIENT_MATTER_WORKFLOW
];

export const requiresClientMatterData = (
  workflowType: PaymentWorkflowType,
): boolean => !WORKFLOWS_WITHOUT_CLIENT_MATTER.includes(workflowType);
